import { concat, difference, isEmpty, join, map, mapValues, startsWith, uniqBy } from 'es-toolkit/compat';
import { FileMetaData, parquetMetadataAsync, parquetReadObjects } from 'hyparquet';
import { compressors } from 'hyparquet-compressors';

import { markerPositivityColumnPrefix } from './constants';
import { getParquetResourceId, measureParquetStage, measureParquetStageAsync } from './instrumentation';
import {
  asyncBufferFromUrlWithGetFallback,
  cachedAsyncBufferWithFixedWindows,
  defaultParquetRangeWindowSize,
} from './rangeBuffer';
import {
  ColumnarParquetData,
  ParquetColumn,
  ParquetFile,
  ParquetMetadataReadRequest,
  ParquetRow,
  ParquetRowsReadRequest,
} from './types';

const getParquetMetadata = async (
  { parquetFileUrl, byteLength, minSize = defaultParquetRangeWindowSize }: ParquetMetadataReadRequest,
  thread: 'main' | 'worker' = 'main'
): Promise<{
  file: Awaited<ReturnType<typeof asyncBufferFromUrlWithGetFallback>>;
  metadata: FileMetaData;
}> => {
  const resourceId = getParquetResourceId(parquetFileUrl);
  const uncachedFile = await asyncBufferFromUrlWithGetFallback(parquetFileUrl, byteLength);
  const file = cachedAsyncBufferWithFixedWindows(uncachedFile, { minSize, resourceId, thread });
  let metadata: FileMetaData;
  try {
    metadata = await measureParquetStageAsync({ resourceId, stage: 'metadata-decode', thread }, () =>
      parquetMetadataAsync(file)
    );
  } catch {
    throw new Error(`Failed to read parquet metadata for ${resourceId}`);
  }
  if (!metadata?.schema) throw new Error(`Parquet metadata has no schema for ${resourceId}`);
  return { file, metadata };
};

export const readParquetMetadataFromUrl = async (
  request: ParquetMetadataReadRequest,
  thread: 'main' | 'worker' = 'main'
): Promise<Pick<FileMetaData, 'schema' | 'num_rows'>> => {
  const { metadata } = await getParquetMetadata(request, thread);
  return { schema: metadata.schema, num_rows: metadata.num_rows };
};

const isCloneSafeValue = (value: unknown, seen: Set<object>): boolean => {
  if (value === null || ['string', 'number', 'boolean', 'bigint', 'undefined'].includes(typeof value)) return true;
  if (typeof value !== 'object' || seen.has(value)) return false;

  seen.add(value);
  const isArray = Array.isArray(value);
  const isPlainObject = Object.getPrototypeOf(value) === Object.prototype;
  if (!isArray && !isPlainObject) return false;
  return Object.values(value).every((nestedValue) => isCloneSafeValue(nestedValue, seen));
};

export const assertCloneSafeRowContext = (rowContext: Record<string, any>) => {
  if (!isCloneSafeValue(rowContext, new Set())) throw new Error('Parquet row context is not clone-safe');
};

export const readParquetFromUrl = async (
  {
    parquetFileUrl,
    columnsForQuery: columns,
    additionalRowContext = {},
    outputFormat,
    ...metadataRequest
  }: ParquetRowsReadRequest,
  thread: 'main' | 'worker' = 'main'
): Promise<ParquetFile> => {
  assertCloneSafeRowContext(additionalRowContext);
  const resourceId = getParquetResourceId(parquetFileUrl);
  const { file, metadata } = await getParquetMetadata({ parquetFileUrl, ...metadataRequest }, thread);
  if (columns && !isEmpty(columns)) {
    const missingColumns = difference(columns, map(metadata.schema, 'name'));
    if (!isEmpty(missingColumns)) {
      throw new Error(
        `Columns ${join(missingColumns, ', ')} not found in parquet resource ${resourceId}. Available columns: ${join(
          map(metadata.schema, 'name'),
          ', '
        )}`
      );
    }
  }

  let decodedRows: Record<string, unknown>[];
  try {
    decodedRows = await measureParquetStageAsync(
      {
        resourceId,
        stage: 'row-decode',
        thread,
        rowCount: Number(metadata.num_rows),
        columnCount: columns?.length ?? metadata.schema.length - 1,
      },
      () => parquetReadObjects({ file, columns, compressors })
    );
  } catch {
    throw new Error(`Failed to decode parquet rows for ${resourceId}`);
  }
  return {
    rows: (outputFormat === 'columnar'
      ? decodedRows
      : map(decodedRows, (row) => ({ ...row, ...additionalRowContext }))) as ParquetRow[],
    metadata,
    columnsQueried: columns ?? map(metadata.schema.slice(1), 'name'),
  };
};

export const normalizeParquetRows = ({
  rows,
  resourceId,
  columnCount,
  thread = 'main',
}: {
  rows?: ParquetRow[];
  resourceId: string;
  columnCount: number;
  thread?: 'main' | 'worker';
}): ParquetRow[] =>
  measureParquetStage(
    { resourceId, stage: 'row-normalization', thread, rowCount: rows?.length ?? 0, columnCount },
    () =>
      map(rows, (row) =>
        mapValues(row, (value, columnName) => {
          if (typeof value === 'bigint') return Number(value);
          if (startsWith(columnName, markerPositivityColumnPrefix)) return Boolean(value);
          return value;
        })
      ) as ParquetRow[]
  );

const columnValidity = (values: unknown[]): Uint8Array | undefined => {
  const validity = Uint8Array.from(values, (value) => Number(value !== null && value !== undefined));
  return validity.includes(0) ? validity : undefined;
};

const normalizeColumnValue = (value: unknown, columnName: string): unknown => {
  if (typeof value === 'bigint') return Number(value);
  if (startsWith(columnName, markerPositivityColumnPrefix)) return Boolean(value);
  return value;
};

/**
 * Converts hyparquet's temporary row objects into the compact representation
 * retained by columnar consumers. The conversion runs in the worker; its typed
 * buffers are then transferred rather than cloned to the main thread.
 *
 * Keep row-oriented output for consumers that need to iterate complete records.
 * This representation is valuable when large datasets are consumed column by
 * column or passed to GPU attributes, and rows are reconstructed only rarely.
 */
export const parquetRowsToColumnarData = ({
  rows,
  columns,
  rowContext = {},
}: {
  rows: Record<string, unknown>[];
  columns: string[];
  rowContext?: Record<string, any>;
}): ColumnarParquetData => {
  const positions = new Float64Array(rows.length * 2);
  for (let index = 0; index < rows.length; index += 1) {
    positions[index * 2] = Number(rows[index]?.X ?? 0);
    positions[index * 2 + 1] = Number(rows[index]?.Y ?? 0);
  }

  const encodedColumns = Object.fromEntries(
    map(
      columns.filter((columnName) => columnName !== 'X' && columnName !== 'Y'),
      (columnName): [string, ParquetColumn] => {
        const values = map(rows, (row) => normalizeColumnValue(row?.[columnName], columnName));
        const validity = columnValidity(values);
        const populatedValues = values.filter((value) => value !== null && value !== undefined);

        if (populatedValues.every((value) => typeof value === 'number')) {
          return [
            columnName,
            {
              kind: 'number',
              values: Float64Array.from(values, (value) => (typeof value === 'number' ? value : 0)),
              validity,
            },
          ];
        }
        if (populatedValues.every((value) => typeof value === 'boolean')) {
          return [
            columnName,
            {
              kind: 'boolean',
              values: Uint8Array.from(values, (value) => Number(Boolean(value))),
              validity,
            },
          ];
        }
        if (populatedValues.every((value) => typeof value === 'string')) {
          const dictionary = Array.from(new Set(populatedValues as string[]));
          const dictionaryIndexes = new Map(map(dictionary, (value, index) => [value, index]));
          return [
            columnName,
            {
              kind: 'dictionary',
              values: Uint32Array.from(values, (value) =>
                typeof value === 'string' ? dictionaryIndexes.get(value) ?? 0 : 0
              ),
              dictionary,
              validity,
            },
          ];
        }
        return [columnName, { kind: 'values', values }];
      }
    )
  );

  return { length: rows.length, positions, columns: encodedColumns, rowContext };
};

export const getColumnarValue = (column: ParquetColumn | undefined, index: number): unknown => {
  if (!column || (column.kind !== 'values' && column.validity?.[index] === 0)) return undefined;
  if (column.kind === 'dictionary') return column.dictionary[column.values[index]];
  if (column.kind === 'boolean') return Boolean(column.values[index]);
  return column.values[index];
};

export const getColumnarParquetRow = (data: ColumnarParquetData, index: number): ParquetRow =>
  ({
    X: data.positions[index * 2],
    Y: data.positions[index * 2 + 1],
    ...mapValues(data.columns, (column) => getColumnarValue(column, index)),
    ...data.rowContext,
  } as ParquetRow);

export const mergeColumnarParquetData = (
  existingData: ColumnarParquetData,
  newData: ColumnarParquetData
): ColumnarParquetData => {
  if (existingData.length !== newData.length) throw new Error('Cannot merge parquet columns with different row counts');
  return {
    length: existingData.length,
    positions: existingData.positions.length ? existingData.positions : newData.positions,
    columns: { ...existingData.columns, ...newData.columns },
    rowContext: { ...existingData.rowContext, ...newData.rowContext },
  };
};

export const mergeParquetFiles = ({
  existingData,
  newData,
  resourceId,
  thread = 'main',
}: {
  existingData: ParquetFile;
  newData: ParquetFile;
  resourceId: string;
  thread?: 'main' | 'worker';
}): ParquetFile => ({
  rows:
    existingData.columnarData && newData.columnarData
      ? []
      : measureParquetStage(
          {
            resourceId,
            stage: 'incremental-merge',
            thread,
            rowCount: existingData.rows.length,
            columnCount: newData.columnsQueried.length,
          },
          () =>
            map(
              existingData.rows,
              (row, index): ParquetRow =>
                ({
                  ...(row || {}),
                  ...(newData.rows[index] || {}),
                } as ParquetRow)
            )
        ),
  columnarData:
    existingData.columnarData && newData.columnarData
      ? mergeColumnarParquetData(existingData.columnarData, newData.columnarData)
      : undefined,
  columnsQueried: concat(existingData.columnsQueried, newData.columnsQueried),
  metadata: {
    ...existingData.metadata,
    schema: uniqBy(concat(existingData.metadata.schema, newData.metadata.schema), 'name'),
  },
});
